/**
 * React Query hooks for Unified Schedules
 *
 * Replaces both useWorkflowSchedules and useSystemTaskSchedules hooks
 * with a single unified interface backed by the Temporal schedule system.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { schedulesApi } from '@/lib/api/schedules';
import type {
  CreateUnifiedScheduleRequest,
  UpdateUnifiedScheduleRequest,
  UnifiedScheduleListParams,
} from '@/types/schedule';

// ============================================================================
// Query Keys
// ============================================================================

export const scheduleKeys = {
  all: ['schedules'] as const,
  lists: () => [...scheduleKeys.all, 'list'] as const,
  list: (params?: UnifiedScheduleListParams) => [...scheduleKeys.lists(), params] as const,
  details: () => [...scheduleKeys.all, 'detail'] as const,
  detail: (id: string) => [...scheduleKeys.details(), id] as const,
  executions: (id: string) => [...scheduleKeys.all, 'executions', id] as const,
};

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Hook for listing schedules with optional type filter
 */
export function useScheduleList(params?: UnifiedScheduleListParams) {
  return useQuery({
    queryKey: scheduleKeys.list(params),
    queryFn: () => schedulesApi.list(params),
  });
}

/**
 * Hook for getting a single schedule by ID
 */
export function useSchedule(scheduleId: string) {
  return useQuery({
    queryKey: scheduleKeys.detail(scheduleId),
    queryFn: () => schedulesApi.get(scheduleId),
    enabled: !!scheduleId,
  });
}

/**
 * Hook for getting execution history for a schedule
 */
export function useScheduleExecutions(scheduleId: string) {
  return useQuery({
    queryKey: scheduleKeys.executions(scheduleId),
    queryFn: () => schedulesApi.getExecutions(scheduleId),
    enabled: !!scheduleId,
  });
}

// ============================================================================
// Mutation Hooks
// ============================================================================

/**
 * Hook for creating a new schedule
 */
export function useCreateSchedule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateUnifiedScheduleRequest) => schedulesApi.create(data),
    onSuccess: (schedule) => {
      queryClient.invalidateQueries({ queryKey: scheduleKeys.lists() });
      toast.success(`调度 "${schedule.name}" 已创建`);
    },
    onError: (error: Error) => {
      toast.error(`创建失败: ${error.message}`);
    },
  });
}

/**
 * Hook for updating an existing schedule
 */
export function useUpdateSchedule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateUnifiedScheduleRequest }) =>
      schedulesApi.update(id, data),
    onSuccess: (schedule) => {
      queryClient.invalidateQueries({ queryKey: scheduleKeys.lists() });
      queryClient.invalidateQueries({ queryKey: scheduleKeys.detail(schedule.id) });
      toast.success(`调度 "${schedule.name}" 已更新`);
    },
    onError: (error: Error) => {
      toast.error(`更新失败: ${error.message}`);
    },
  });
}

/**
 * Hook for deleting a schedule
 */
export function useDeleteSchedule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (scheduleId: string) => schedulesApi.delete(scheduleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: scheduleKeys.lists() });
      toast.success('调度已删除');
    },
    onError: (error: Error) => {
      toast.error(`删除失败: ${error.message}`);
    },
  });
}

/**
 * Hook for toggling schedule enabled/disabled state
 */
export function useToggleSchedule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (scheduleId: string) => schedulesApi.toggle(scheduleId),
    onSuccess: (schedule) => {
      queryClient.invalidateQueries({ queryKey: scheduleKeys.lists() });
      queryClient.invalidateQueries({ queryKey: scheduleKeys.detail(schedule.id) });
      toast.success(schedule.is_enabled ? '已启用' : '已禁用');
    },
    onError: (error: Error) => {
      toast.error(`操作失败: ${error.message}`);
    },
  });
}

/**
 * Hook for manually triggering a schedule execution
 */
export function useTriggerSchedule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (scheduleId: string) => schedulesApi.trigger(scheduleId),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: scheduleKeys.lists() });
      if (response.schedule_id) {
        queryClient.invalidateQueries({ queryKey: scheduleKeys.executions(response.schedule_id) });
      }
      toast.success('执行已触发');
    },
    onError: (error: Error) => {
      toast.error(`触发失败: ${error.message}`);
    },
  });
}

/** Fetch paginated run history for a schedule */
export function useScheduleRuns(
  scheduleId: string,
  params?: { limit?: number; offset?: number },
  enabled = true,
) {
  return useQuery({
    queryKey: ['scheduleRuns', scheduleId, params],
    queryFn: () => schedulesApi.listRuns(scheduleId, params),
    enabled: enabled && !!scheduleId,
    staleTime: 10_000,
  });
}

/** Fetch a single run detail (with polling while status = running) */
export function useScheduleRunDetail(
  scheduleId: string,
  runId: string | null,
  enabled = true,
) {
  return useQuery({
    queryKey: ['scheduleRunDetail', scheduleId, runId],
    queryFn: () => schedulesApi.getRun(scheduleId, runId!),
    enabled: enabled && !!scheduleId && !!runId,
    staleTime: 0,
    refetchInterval: (query) => {
      const run = query.state.data as any;
      if (!run) return false;
      if (run.status === 'running') return 3000;
      return false;
    },
  });
}

/** Retry failed users mutation */
export function useRetryFailedRun() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ scheduleId, runId }: { scheduleId: string; runId: string }) =>
      schedulesApi.retryFailed(scheduleId, runId),
    onSuccess: (_, { scheduleId }) => {
      queryClient.invalidateQueries({ queryKey: ['scheduleRuns', scheduleId] });
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
      toast.success('重跑已触发');
    },
    onError: (error: Error) => {
      toast.error(`重跑失败: ${error.message}`);
    },
  });
}

export function useCancelRun() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ scheduleId, runId }: { scheduleId: string; runId: string }) =>
      schedulesApi.cancelRun(scheduleId, runId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['scheduleRunDetail', variables.scheduleId, variables.runId] });
      queryClient.invalidateQueries({ queryKey: ['scheduleRuns', variables.scheduleId] });
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
      toast.success('Run cancelled');
    },
    onError: (error: Error) => {
      toast.error(`Cancel failed: ${error.message}`);
    },
  });
}
